import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Building2, ShieldCheck, UserPlus, Pencil, KeyRound, Power } from "lucide-react";
import { toast } from "sonner";

function AdminsDialog({ open, onOpenChange, cliente, allSucursales }) {
  const [admins, setAdmins] = useState([]);
  const [sucursalesDelCliente, setSucursalesDelCliente] = useState([]);
  const [form, setForm] = useState({ nombre: "", email: "", password: "", sucursales_ids: [] });
  const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [reassignFor, setReassignFor] = useState(null);
  const [reassignSids, setReassignSids] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [pwdFor, setPwdFor] = useState(null);
  const [pwd, setPwd] = useState("");
  const [tempBanner, setTempBanner] = useState(null); // { email, password, sucursales }

  const load = async () => {
    if (!cliente?.id) return;
    try {
      const [a, s] = await Promise.all([
        api.get(`/admin/clientes/${cliente.id}/admins`),
        api.get(`/admin/sucursales`),
      ]);
      setAdmins(a.data || []);
      const sucs = (s.data || []).filter((x) => x.cliente_id === cliente.id);
      setSucursalesDelCliente(sucs);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => {
    if (open) {
      load();
      setForm({ nombre: "", email: "", password: "", sucursales_ids: [] });
      setShowCreate(false);
      setReassignFor(null);
      setEditing(null);
      setPwdFor(null);
      setTempBanner(null);
    }
  }, [open, cliente]);

  const toggleSucCreate = (id) => {
    const cur = form.sucursales_ids;
    setForm({ ...form, sucursales_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
  };
  const toggleSucReassign = (id) => {
    const cur = reassignSids;
    setReassignSids(cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  };

  const crearAdmin = async () => {
    if (!form.nombre.trim() || !form.email.trim() || !form.password) return toast.error("Completa los datos");
    if (!form.sucursales_ids.length) return toast.error("Asigna al menos una sucursal");
    setSaving(true);
    try {
      const r = await api.post(`/admin/clientes/${cliente.id}/admins`, {
        nombre: form.nombre.trim(),
        email: form.email.toLowerCase().trim(),
        password: form.password,
        sucursales_ids: form.sucursales_ids,
      });
      const sucNames = sucursalesDelCliente.filter((s) => form.sucursales_ids.includes(s.id)).map((s) => s.nombre).join(", ");
      setTempBanner({
        accion: "creado",
        email: r.data.email,
        password: form.password,
        sucursales: sucNames,
      });
      toast.success(`Administrador "${form.nombre}" creado correctamente.`);
      setShowCreate(false);
      setForm({ nombre: "", email: "", password: "", sucursales_ids: [] });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const beginReassign = (a) => {
    setReassignFor(a);
    setReassignSids(a.sucursales_ids || (a.sucursal_id ? [a.sucursal_id] : []));
  };
  const guardarReassign = async () => {
    if (!reassignSids.length) return toast.error("Asigna al menos una sucursal");
    setSaving(true);
    try {
      await api.patch(`/admin/users/${reassignFor.id}/sucursales`, { sucursales_ids: reassignSids });
      toast.success("Sucursales reasignadas correctamente.");
      setReassignFor(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const beginEdit = (a) => {
    setEditing(a);
    setEditForm({ nombre: a.nombre });
  };
  const guardarEdit = async () => {
    if (!editForm.nombre?.trim()) return toast.error("Nombre obligatorio");
    setSaving(true);
    try {
      await api.patch(`/auth/users/${editing.id}`, { nombre: editForm.nombre.trim() });
      toast.success(`Administrador "${editForm.nombre}" actualizado correctamente.`);
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const toggleActivo = async (a) => {
    try {
      if (a.active) {
        await api.delete(`/auth/users/${a.id}`);
        toast.success(`Administrador "${a.nombre}" desactivado.`);
      } else {
        await api.post(`/auth/users/${a.id}/activar`);
        toast.success(`Administrador "${a.nombre}" reactivado.`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const beginPwd = (a) => { setPwdFor(a); setPwd(""); };
  const guardarPwd = async () => {
    if (!pwd) return toast.error("Ingresa nueva contraseña");
    setSaving(true);
    try {
      await api.patch(`/auth/users/${pwdFor.id}`, { password: pwd });
      toast.success(`Contraseña actualizada correctamente.`);
      setTempBanner({
        accion: "restablecido",
        email: pwdFor.email,
        password: pwd,
        sucursales: (pwdFor.sucursales_nombres || []).join(", "),
      });
      setPwdFor(null);
      setPwd("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!cliente) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />Administradores de {cliente.nombre_comercial}
          </DialogTitle>
        </DialogHeader>

        {tempBanner && (
          <div className="rounded-lg border-2 border-emerald-500/50 bg-emerald-50 dark:bg-emerald-950/30 p-4 space-y-1.5" data-testid="temp-pwd-banner">
            <div className="flex items-start justify-between">
              <div className="font-bold text-emerald-700 dark:text-emerald-300 flex items-center gap-1">
                <KeyRound className="h-4 w-4" />
                Administrador {tempBanner.accion} correctamente
              </div>
              <button className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setTempBanner(null)}>✕</button>
            </div>
            <div className="text-sm">
              <div><span className="text-muted-foreground">Email:</span> <span className="font-mono font-bold">{tempBanner.email}</span></div>
              <div><span className="text-muted-foreground">Contraseña temporal:</span> <span className="font-mono font-bold bg-emerald-200 dark:bg-emerald-900/60 px-2 py-0.5 rounded">{tempBanner.password}</span></div>
              {tempBanner.sucursales && <div><span className="text-muted-foreground">Sucursales:</span> {tempBanner.sucursales}</div>}
            </div>
            <div className="text-[10px] text-muted-foreground italic mt-1">Anota esta contraseña — no volverá a aparecer.</div>
          </div>
        )}

        {!showCreate && !reassignFor && !editing && !pwdFor && (
          <>
            <div className="space-y-2 mt-2">
              {admins.length === 0 ? (
                <div className="text-sm text-muted-foreground py-4 text-center">
                  Este cliente aún no tiene administradores. Crea el primero.
                </div>
              ) : admins.map((a) => (
                <div key={a.id} className={`rounded-md border p-3 ${a.active ? "border-border" : "border-border opacity-60"}`} data-testid={`cli-admin-${a.id}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-bold flex items-center gap-2">
                        {a.nombre}
                        {a.active
                          ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-200 text-emerald-900 dark:bg-emerald-800 dark:text-emerald-100 font-bold uppercase tracking-wider">Activo</span>
                          : <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-200 text-red-900 dark:bg-red-800 dark:text-red-100 font-bold uppercase tracking-wider">Inactivo</span>}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 font-mono">{a.email}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        <strong>Sucursales:</strong> {(a.sucursales_nombres || []).join(", ") || "—"}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      <Button size="sm" variant="outline" onClick={() => beginEdit(a)} data-testid={`editar-admin-${a.id}`}>
                        <Pencil className="h-3 w-3 mr-1" />Editar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => beginPwd(a)} data-testid={`pwd-admin-${a.id}`}>
                        <KeyRound className="h-3 w-3 mr-1" />Contraseña
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => beginReassign(a)} data-testid={`reasignar-${a.id}`}>
                        Reasignar
                      </Button>
                      <Button size="sm" variant={a.active ? "ghost" : "default"} onClick={() => toggleActivo(a)} data-testid={`toggle-admin-${a.id}`}>
                        <Power className="h-3 w-3 mr-1" />{a.active ? "Desactivar" : "Activar"}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
              <Button onClick={() => setShowCreate(true)} data-testid="nuevo-admin-cliente-btn">
                <UserPlus className="h-4 w-4 mr-1" />Nuevo administrador
              </Button>
            </DialogFooter>
          </>
        )}

        {showCreate && (
          <>
            <div className="space-y-3 mt-2">
              <div><Label>Nombre completo *</Label><Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="adm-nombre" /></div>
              <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="adm-email" /></div>
              <div>
                <Label>Contraseña temporal *</Label>
                <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="adm-password" />
                <p className="text-[10px] text-muted-foreground mt-1">Min 8: mayúscula, minúscula, número y símbolo. Se mostrará tras crear.</p>
              </div>
              <div>
                <Label>Sucursales a administrar *</Label>
                <div className="mt-2 space-y-1 max-h-48 overflow-y-auto rounded-md border border-border p-2">
                  {sucursalesDelCliente.length === 0 ? (
                    <div className="text-xs text-muted-foreground p-2">Este cliente no tiene sucursales todavía.</div>
                  ) : sucursalesDelCliente.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-secondary/50 cursor-pointer">
                      <Checkbox
                        checked={form.sucursales_ids.includes(s.id)}
                        onCheckedChange={() => toggleSucCreate(s.id)}
                        data-testid={`adm-suc-${s.id}`}
                      />
                      <span className="text-sm">{s.nombre}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
              <Button onClick={crearAdmin} disabled={saving} data-testid="adm-crear-btn">{saving ? "Creando…" : "Crear administrador"}</Button>
            </DialogFooter>
          </>
        )}

        {editing && (
          <>
            <div className="space-y-3 mt-2">
              <div className="text-sm text-muted-foreground">Editando: <span className="font-mono">{editing.email}</span></div>
              <div><Label>Nombre completo</Label><Input value={editForm.nombre || ""} onChange={(e) => setEditForm({ ...editForm, nombre: e.target.value })} data-testid="adm-edit-nombre" /></div>
              <p className="text-xs text-muted-foreground italic">Para reasignar sucursales o cambiar contraseña usa las acciones específicas.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button onClick={guardarEdit} disabled={saving} data-testid="adm-edit-save">{saving ? "Guardando…" : "Guardar"}</Button>
            </DialogFooter>
          </>
        )}

        {pwdFor && (
          <>
            <div className="space-y-3 mt-2">
              <div className="text-sm">Restablecer contraseña de <strong>{pwdFor.nombre}</strong> <span className="text-xs text-muted-foreground font-mono">({pwdFor.email})</span></div>
              <div>
                <Label>Nueva contraseña temporal</Label>
                <Input type="text" value={pwd} onChange={(e) => setPwd(e.target.value)} autoFocus data-testid="adm-pwd-input" />
                <p className="text-[10px] text-muted-foreground mt-1">Min 8: mayúscula, minúscula, número y símbolo.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPwdFor(null)}>Cancelar</Button>
              <Button onClick={guardarPwd} disabled={saving} data-testid="adm-pwd-save">{saving ? "Guardando…" : "Restablecer contraseña"}</Button>
            </DialogFooter>
          </>
        )}

        {reassignFor && (
          <>
            <div className="space-y-3 mt-2">
              <div className="text-sm">Reasignando sucursales para <strong>{reassignFor.nombre}</strong></div>
              <div>
                <Label>Sucursales asignadas</Label>
                <div className="mt-2 space-y-1 max-h-48 overflow-y-auto rounded-md border border-border p-2">
                  {sucursalesDelCliente.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-secondary/50 cursor-pointer">
                      <Checkbox
                        checked={reassignSids.includes(s.id)}
                        onCheckedChange={() => toggleSucReassign(s.id)}
                        data-testid={`reasignar-suc-${s.id}`}
                      />
                      <span className="text-sm">{s.nombre}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReassignFor(null)}>Cancelar</Button>
              <Button onClick={guardarReassign} disabled={saving} data-testid="reasignar-save-btn">{saving ? "Guardando…" : "Guardar"}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Clientes() {
  const [clientes, setClientes] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [adminsOpen, setAdminsOpen] = useState(false);
  const [target, setTarget] = useState(null);

  const load = async () => {
    const [c, s] = await Promise.all([
      api.get("/admin/clientes"),
      api.get("/admin/sucursales"),
    ]);
    setClientes(c.data);
    setSucursales(s.data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({}); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      nombre_comercial: c.nombre_comercial || "",
      razon_social: c.razon_social || "",
      rfc: c.rfc || "",
      telefono: c.telefono || "",
      email: c.email || "",
      direccion: c.direccion || "",
      notas: c.notas || "",
      active: c.active,
    });
    setOpen(true);
  };

  const onSave = async () => {
    if (!form.nombre_comercial?.trim()) return toast.error("Nombre requerido");
    try {
      if (editing?.id) {
        await api.patch(`/admin/clientes/${editing.id}`, form);
        toast.success("Cliente actualizado correctamente.");
      } else {
        await api.post("/admin/clientes", form);
        toast.success(`Cliente "${form.nombre_comercial}" creado correctamente.`);
      }
      setOpen(false); setForm({}); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActiveCliente = async (c) => {
    try {
      if (c.active) {
        await api.delete(`/admin/clientes/${c.id}`);
        toast.success(`Cliente "${c.nombre_comercial}" desactivado.`);
      } else {
        await api.patch(`/admin/clientes/${c.id}`, { active: true });
        toast.success(`Cliente "${c.nombre_comercial}" reactivado.`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div>
          <h2 className="text-3xl font-black tracking-tight">Clientes</h2>
          <p className="text-sm text-muted-foreground mt-1">Gestiona los clientes de la plataforma y sus administradores.</p>
        </div>
        <Button onClick={openCreate} data-testid="new-cliente-btn"><Plus className="h-4 w-4 mr-2" />Nuevo cliente</Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Editar cliente" : "Nuevo cliente"}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            <div><Label>Nombre comercial *</Label><Input value={form.nombre_comercial || ""} onChange={(e) => setForm({ ...form, nombre_comercial: e.target.value })} data-testid="cliente-nombre-input" /></div>
            <div><Label>Razón social</Label><Input value={form.razon_social || ""} onChange={(e) => setForm({ ...form, razon_social: e.target.value })} /></div>
            <div><Label>RFC</Label><Input value={form.rfc || ""} onChange={(e) => setForm({ ...form, rfc: e.target.value })} data-testid="cliente-rfc-input" /></div>
            <div><Label>Teléfono</Label><Input value={form.telefono || ""} onChange={(e) => setForm({ ...form, telefono: e.target.value })} data-testid="cliente-telefono-input" /></div>
            <div><Label>Email</Label><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Dirección</Label><Input value={form.direccion || ""} onChange={(e) => setForm({ ...form, direccion: e.target.value })} /></div>
            <div><Label>Notas</Label><Textarea value={form.notas || ""} onChange={(e) => setForm({ ...form, notas: e.target.value })} /></div>
            {editing?.id && (
              <label className="flex items-center gap-2 cursor-pointer pt-2">
                <Checkbox checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: !!v })} data-testid="cliente-active-check" />
                <span className="text-sm">Cliente activo</span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={onSave} data-testid="save-cliente-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>RFC</TableHead>
                <TableHead>Teléfono</TableHead>
                <TableHead>Sucursales</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clientes.map((c) => (
                <TableRow key={c.id} data-testid={`cliente-row-${c.id}`}>
                  <TableCell className="font-semibold flex items-center gap-2"><Building2 className="h-4 w-4 text-muted-foreground" />{c.nombre_comercial}</TableCell>
                  <TableCell className="text-muted-foreground">{c.rfc || "-"}</TableCell>
                  <TableCell>{c.telefono || "-"}</TableCell>
                  <TableCell>{c.sucursales_count}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded text-xs font-bold ${c.active ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-200" : "bg-red-100 text-red-700"}`}>
                      {c.active ? "ACTIVO" : "INACTIVO"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`edit-cliente-${c.id}`}>
                      <Pencil className="h-3 w-3 mr-1" />Editar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setTarget(c); setAdminsOpen(true); }} data-testid={`admins-cliente-${c.id}`}>
                      <ShieldCheck className="h-3 w-3 mr-1" />Administradores
                    </Button>
                    <Button size="sm" variant={c.active ? "ghost" : "default"} onClick={() => toggleActiveCliente(c)} data-testid={`toggle-cliente-${c.id}`}>
                      <Power className="h-3 w-3 mr-1" />{c.active ? "Desactivar" : "Activar"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {clientes.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No hay clientes</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AdminsDialog open={adminsOpen} onOpenChange={setAdminsOpen} cliente={target} allSucursales={sucursales} />
    </div>
  );
}
